import React, { useCallback } from 'react';
import { useNavigate, generatePath } from 'react-router-dom';
import { CircleCheckIcon } from 'lucide-react';

import { EventType, EditableSession } from 'types/event.types';
import { DASHBOARD_EVENT_CHECKIN_MANAGER_ROUTE } from 'constants/routes';
import { Banner, BannerIcon, BannerTitle } from '@epoch-ui/components/Banner';
import { Icon } from '@epoch-ui/components/Icon';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import {
  useEstimatedAttendanceModal,
  EventEstimatedAttendanceModal
} from '@event-editor/components/EventEstimatedAttendanceModal';

interface GuestCheckInBannerProps {
  session?: EditableSession;
}

const GuestCheckInBanner: React.FC<GuestCheckInBannerProps> = ({ session }) => {
  const event = useEditableEvent();
  const navigate = useNavigate();
  const { modalProps, open: openEstimatedAttendanceModal } = useEstimatedAttendanceModal({
    session
  });

  const hasCheckedInGuests =
    (session?.totalCheckedInGuests ?? 0) > 0 || (event.totalCheckedInGuests ?? 0) > 0;
  const isUsingEstimatedAttendees =
    session?.isUsingEstimatedAttendees || event.isUsingEstimatedAttendees;

  const goToCheckInManager = useCallback(() => {
    navigate(
      generatePath(DASHBOARD_EVENT_CHECKIN_MANAGER_ROUTE, {
        id: String(event.id!),
        sessionId:
          event.eventType === EventType.MULTI_SESSIONS && session ? String(session.id) : null
      })
    );
  }, [event.id, event.eventType, session, navigate]);

  const handleBannerClick = useCallback(() => {
    if (isUsingEstimatedAttendees) {
      openEstimatedAttendanceModal();
      return;
    }

    goToCheckInManager();
  }, [event.isUsingEstimatedAttendees, session?.isUsingEstimatedAttendees, goToCheckInManager]);

  if (event.eventType === EventType.MULTI_SESSIONS && !session) {
    return null;
  }

  return (
    <Banner
      variant="success"
      className="tw-h-16 tw-cursor-pointer tw-p-3"
      onClick={handleBannerClick}
    >
      <BannerIcon icon={CircleCheckIcon} />
      <BannerTitle className="tw-font-medium">
        {isUsingEstimatedAttendees ? (
          'Update estimated attendees'
        ) : (
          <div className="tw-flex tw-flex-col">
            <p className="tw-m-0">{hasCheckedInGuests ? 'Update check-in' : 'Begin check-in'}</p>
            {!!session && <p className="tw-m-0 tw-leading-5">{session.title}</p>}
          </div>
        )}
      </BannerTitle>
      <Icon name="arrow-right" size={16} />

      {isUsingEstimatedAttendees && <EventEstimatedAttendanceModal {...modalProps} />}
    </Banner>
  );
};

export { GuestCheckInBanner };
